import React from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Field } from "formik";
import { createViewTypeHelperText } from "./createViewTypeHelperText";
import { useAddSiteOnboardingStore } from "../../pages/AddSite/addSiteOnboardingStore";
import { withProtocolLabels } from "./streamAddress";

function normalizeGroupSearch(s: string) {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

type GroupOptionRow = { id: string | number; name: string; index: number };

export const ChoiceFieldType = ({
  config,
  formik,
  setErrorState,
  setChoicesChanger,
  choicesChanger,
}: {
  config: any;
  formik: any;
  setErrorState: any;
  setChoicesChanger: any;
  choicesChanger: any;
}) => {
  // ENG-1924: plain-language Protocol labels; the stored value is unchanged.
  const options =
    config.field === "protocol" || config.id === "protocol"
      ? withProtocolLabels(config.options)
      : config.options;
  const isGroupField = config.id === "group";
  const optionsIds = new Map();
  const optionNames = new Map();
  options.forEach((option: any) => {
    optionsIds.set(option[0], option[1]);
  });
  options.forEach((option: any) => {
    optionNames.set(option[1], option[0]);
  });

  // `config.options` entries are [displayName, groupId] tuples. MUI Autocomplete
  // uses { id, name, index } on the option objects — id/name map from tuple[1] /
  // tuple[0], not the same as `config.id` ("group"), which is the form control key.
  const groupOptionRows = React.useMemo((): GroupOptionRow[] => {
    return options.map((option: string[], index: number) => ({
      id: option[1],
      name: String(option[0]),
      index,
    }));
  }, [options]);

  const [groupFilterInput, setGroupFilterInput] = React.useState("");

  const groupFieldValue = formik.values[config.field];

  const filteredGroupOptions = React.useMemo(() => {
    const q = normalizeGroupSearch(groupFilterInput);
    if (!q) {
      return groupOptionRows;
    }
    return groupOptionRows.filter((opt: GroupOptionRow) => {
      const n = normalizeGroupSearch(String(opt.name));
      return n.includes(q);
    });
  }, [groupFilterInput, groupOptionRows]);

  /** When a group is selected, keep controlled input text in sync with the label (incl. after options load). */
  React.useEffect(() => {
    if (!isGroupField) return;
    const gid = groupFieldValue;
    if (gid === "" || gid == null) {
      return;
    }
    const row = groupOptionRows.find(
      (r) => String(r.id) === String(gid)
    );
    if (row) {
      setGroupFilterInput(String(row.name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when selection id or option count changes; `groupOptionRows` intentionally omitted (parent may pass new `options` ref each render).
  }, [
    isGroupField,
    config.field,
    groupFieldValue,
    groupOptionRows.length,
  ]);

  const [helperText, setHelperText] = React.useState(config.helperText);

  React.useEffect(() => {
    if (config.validation) {
      setErrorState((previousState: any) => {
        return { ...previousState, [config.id]: true };
      });
    }
  }, []);

  return (
    <div key={config.id}>
      <Autocomplete
        disablePortal
        id={config.id}
        options={
          isGroupField
            ? filteredGroupOptions
            : options.map((option: string[]) => {
                return option[0];
              })
        }
        {...(isGroupField
          ? {
              inputValue: groupFilterInput,
              getOptionLabel: (option: {
                id: string | number;
                name: string;
                index: number;
              }) =>
                option && typeof option === "object"
                  ? option.name || ""
                  : "",
              isOptionEqualToValue: (
                option: { id: string | number; name: string; index: number },
                value: { id: string | number; name: string; index: number } | null
              ) => value != null && option.id === value.id,
              // MUI's built-in filterOptions can receive an empty `inputValue` when
              // `inputValueIsSelectedValue && inputPristine` (see useAutocomplete.js),
              // which would show the full unfiltered list. We filter ourselves from
              // `groupFilterInput` (synced via onInputChange) and bypass MUI filtering.
              filterOptions: (opts: GroupOptionRow[]) => opts,
              onInputChange: (_event, value: string, reason: string) => {
                if (reason === "input" || reason === "clear") {
                  setGroupFilterInput(reason === "clear" ? "" : value);
                } else if (reason === "reset") {
                  // MUI can emit reset + "" immediately after each "input" while typing;
                  // applying that cleared our filter and showed all rows (see debug-9c4032.log).
                  if (typeof value === "string" && value !== "") {
                    setGroupFilterInput(value);
                  }
                }
              },
              value:
                formik.values[config.field] !== "" &&
                formik.values[config.field] != null
                  ? {
                      id: formik.values[config.field],
                      name: optionNames.get(formik.values[config.field]),
                      index: options.findIndex(
                        (o: string[]) =>
                          o[1] === formik.values[config.field] ||
                          String(o[1]) ===
                            String(formik.values[config.field])
                      ),
                    }
                  : null,
              renderOption: (
                props: React.HTMLAttributes<HTMLLIElement>,
                option: GroupOptionRow
              ) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              ),
            }
          : {
              value: optionNames.get(formik.values[config.field]),
            })}
        onChange={(event: any, newInputValue: any) => {
          if (isGroupField) {
            formik.values[config.field] =
              newInputValue != null && typeof newInputValue === "object"
                ? newInputValue.id
                : "";
            if (
              newInputValue == null ||
              typeof newInputValue !== "object"
            ) {
              setGroupFilterInput("");
            }
          } else {
            formik.values[config.field] = optionsIds.get(newInputValue);
          }
          setChoicesChanger(!choicesChanger);
          if (
            config.id === "default_alarm" &&
            newInputValue === "Webhooks" &&
            location.href.includes("addsite")
          ) {
            localStorage.setItem(
              "showWebHooksOnboardingScreen",
              JSON.stringify(true)
            );
          }
          if (
            config.id === "default_alarm" &&
            newInputValue !== "Webhooks" &&
            location.href.includes("addsite")
          ) {
            localStorage.setItem(
              "showWebHooksOnboardingScreen",
              JSON.stringify(false)
            );
          }
          if (
            config.id === "camera_view" &&
            location.href.includes("addcamera")
          ) {
            createViewTypeHelperText(newInputValue, setHelperText);
          }
          if (config.id === "default_alarm") {
            if (newInputValue === "Actuate Only") {
              localStorage.setItem("immixAlertsSite", "false");
              localStorage.setItem("sentinelAlertsSite", "false");
              localStorage.setItem("sureviewAlertsSite", "false");
              localStorage.setItem("diceAlertsSite", "false");
            } else if (newInputValue === "Immix") {
              localStorage.setItem("immixAlertsSite", "true");
              localStorage.setItem("sentinelAlertsSite", "false");
              localStorage.setItem("sureviewAlertsSite", "false");
              localStorage.setItem("diceAlertsSite", "false");
            } else if (newInputValue === "Dice") {
              localStorage.setItem("immixAlertsSite", "false");
              localStorage.setItem("sentinelAlertsSite", "false");
              localStorage.setItem("sureviewAlertsSite", "false");
              localStorage.setItem("diceAlertsSite", "true");
            } else if (newInputValue === "SureView") {
              localStorage.setItem("immixAlertsSite", "false");
              localStorage.setItem("sureviewAlertsSite", "true");
              localStorage.setItem("sentinelAlertsSite", "false");
              localStorage.setItem("diceAlertsSite", "false");
            } else if (newInputValue === "Sentinel") {
              localStorage.setItem("immixAlertsSite", "false");
              localStorage.setItem("sureviewAlertsSite", "false");
              localStorage.setItem("sentinelAlertsSite", "true");
              localStorage.setItem("diceAlertsSite", "false");
            } else {
              localStorage.setItem("immixAlertsSite", "false");
              localStorage.setItem("sentinelAlertsSite", "false");
              localStorage.setItem("sureviewAlertsSite", "false");
              localStorage.setItem("diceAlertsSite", "false");
            }
          }
          if (config.id === "group" && location.href.includes("addsite")) {
            const groupLabel =
              newInputValue != null && typeof newInputValue === "object"
                ? newInputValue.name
                : "";
            localStorage.setItem("groupSelected", groupLabel);
          }
          if (
            config.id === "wireguard" &&
            location.href.includes("addsite")
          ) {
            const store = useAddSiteOnboardingStore.getState();
            store.setNeedsWireGuardOnboarding(!!newInputValue);
            store.setSelectedWireGuardServer(newInputValue || null);
          }
        }}
        sx={{ width: 350 }}
        renderInput={(params) =>
          isGroupField ? (
            <TextField
              {...params}
              variant="standard"
              label={config.label}
              autoComplete="off"
              id={config.id}
              helperText={helperText}
            />
          ) : (
            <Field
              {...params}
              variant="standard"
              label={config.label}
              autoComplete="off"
              id={config.id}
              name={config.id}
              helperText={helperText}
              as={TextFieldComponent}
            />
          )
        }
      />
    </div>
  );
};

const TextFieldComponent = (props: any) => <TextField {...props} />;

